import type { ExclusiveLocks, Kv } from './browser';
import { QUERY_CACHE_KEY, parseAccount, type StoredClient } from './cache-account';
import { legacySessionAccount } from './drafts';
import type { FailedWrite, FailedWriteStore } from './failed-writes';
import { pathOf } from './offline-replay';
import { idOf, keyOf } from './idempotency';
import { isReplayable, parsePendingWrite, type PendingWrite } from './pending-writes';
import { accountRef, sameAccount, type AccountRef } from './account';

/**
 * Moving an earlier release's queued writes to the account that made them (MAP 225b), once.
 *
 * Before 225b a paused replayable write was saved INSIDE the query-cache blob and rebuilt by
 * TanStack's `hydrate()` at the next start, whoever was signed in by then. Now each write
 * has a row of its own, so the old ones are moved out of the blob, account and all, and the
 * blob never hands a write to TanStack again.
 *
 * It runs inside the persister's `restoreClient`, right after the drafts, because TanStack
 * calls `restoreClient` FIRST and only then checks the blob's age and buster and may remove
 * it: so the move always happens before the blob can go.
 *
 * Whose they are: the 225a blob's label (`account`, the account its queries belong to); else
 * a pre-225a blob's persisted `me.session`, if signed in to a shop; else nobody, and the
 * write is discarded (there is no claim screen).
 *
 * Copying is only-if-absent, one transaction per key, and the CLEANED blob is the "done"
 * marker: a second tab, or a start after a crash halfway, re-reads and does nothing new.
 * Under a Web Lock where there is one; after 5 s, or with none, it runs unlocked, which the
 * only-if-absent copy makes safe.
 *
 * If the store will not take them (it will not open, the disk is full), the writes are
 * CARRIED: returned to the cache account, which keeps them in every write of the blob
 * (`legacyWrites`, already in their row shape) until the next start moves them.
 */

export const LEGACY_WRITES_LOCK = 'servwell-legacy-writes';
/** How long a start waits for another tab's move before moving them itself (safe). */
const LOCK_WAIT_MS = 5_000;

export interface LegacyWritesEnv {
  /** Where the query-cache blob lives. */
  kv: Pick<Kv, 'get' | 'update'>;
  key?: IDBValidKey;
  /** The pending-write store: copy only if absent, in ONE readwrite transaction. */
  pending: { putIfAbsent(row: PendingWrite): Promise<void> };
  /** The failed-write store, for an earlier release's refusals recorded with no account. */
  failed: FailedWriteStore;
  locks: ExclusiveLocks | undefined;
  /** The give-up signal for the lock wait. */
  lockSignal?: () => AbortSignal;
}

/**
 * The old writes a blob holds, as rows, and how many could be given to nobody. Carried rows
 * (`legacyWrites`) keep the account they were given when they were first moved.
 */
export function legacyRowsOf(blob: StoredClient | undefined): {
  account: AccountRef | null;
  rows: PendingWrite[];
  discarded: number;
} {
  const account = accountRef(parseAccount(blob?.account)) ?? legacySessionAccount(blob);
  const rows: PendingWrite[] = [];
  let discarded = 0;
  for (const raw of Array.isArray(blob?.legacyWrites) ? blob.legacyWrites : []) {
    const row = parsePendingWrite(raw);
    if (row) rows.push(row);
    else discarded += 1;
  }
  const mutations = Array.isArray(blob?.clientState?.mutations)
    ? blob.clientState.mutations
    : [];
  for (const m of mutations) {
    if (typeof m !== 'object' || m === null) {
      discarded += 1;
      continue;
    }
    const path = pathOf(m.mutationKey);
    const variables = m.state?.variables;
    const key = keyOf(variables);
    if (!account || !isReplayable(path) || !key) {
      discarded += 1;
      continue;
    }
    rows.push({
      id: idOf(key),
      ...key,
      path,
      variables: variables as Record<string, unknown>,
      madeBy: account,
      createdAt: typeof m.state.submittedAt === 'number' ? m.state.submittedAt : 0,
      source: 'migrated',
    });
  }
  return { account, rows, discarded };
}

/**
 * Move them. Resolves with the rows it could not move (to carry), or null. Never rejects,
 * whatever this browser lacks: the restore it runs inside must not fail over it, because a
 * restore that throws makes TanStack remove the blob, and with it the only copy of these
 * writes. Anything unforeseen carries every row the blob holds.
 */
export async function moveLegacyWrites(
  stored: StoredClient | undefined,
  env: LegacyWritesEnv,
): Promise<readonly PendingWrite[] | null> {
  try {
    return await moveUnderLock(stored, env);
  } catch (err) {
    console.warn(
      '[servwell] old queued writes could not be moved; kept until next time',
      err,
    );
    return carriedFrom(stored);
  }
}

/** Every row a blob holds, to carry: what a start that could not move them keeps. */
export function carriedFrom(
  stored: StoredClient | undefined,
): readonly PendingWrite[] | null {
  try {
    const { rows } = legacyRowsOf(stored);
    return rows.length ? rows : null;
  } catch {
    return null;
  }
}

async function moveUnderLock(
  stored: StoredClient | undefined,
  env: LegacyWritesEnv,
): Promise<readonly PendingWrite[] | null> {
  const run = () => moveUnlocked(stored, env);
  const signal = env.locks ? (env.lockSignal?.() ?? giveUpAfter(LOCK_WAIT_MS)) : null;
  // No lock manager, or no way to stop waiting for one (an older browser): unlocked, which
  // the only-if-absent copy makes safe.
  if (!env.locks || !signal) return run();
  try {
    return await env.locks.request(LEGACY_WRITES_LOCK, { signal }, run);
  } catch {
    // Another tab is taking too long, or the lock itself failed: run unlocked.
    console.warn('[servwell] moving old queued writes without waiting for another tab');
    return run();
  }
}

/**
 * A signal that aborts after `ms`, in any browser that has signals: `AbortSignal.timeout`
 * is missing before iOS 16. Null if there is no way to make one.
 */
function giveUpAfter(ms: number): AbortSignal | null {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  if (typeof AbortController === 'undefined') return null;
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

async function moveUnlocked(
  stored: StoredClient | undefined,
  env: LegacyWritesEnv,
): Promise<readonly PendingWrite[] | null> {
  const key = env.key ?? QUERY_CACHE_KEY;
  // 1. Re-read the blob: another tab may have moved them and cleaned it already.
  const blob = await reread(env, key, stored);
  const { account, rows, discarded } = legacyRowsOf(blob);
  // 2. Refusals recorded with no account go to the device's last account, marked as maybe
  //    not theirs, or are removed when no account is known. Before the copy, so a full store
  //    for one does not hold up the other.
  await settleNoAccountRefusals(account ?? soleAccount(rows), env.failed);
  if (rows.length === 0 && discarded === 0) return null;
  // 3. Copy only if absent; anything short of all of them is carried, all of them.
  const carried = await copyRows(rows, env);
  if (carried) return carried;
  if (discarded > 0) {
    console.warn(
      `[servwell] ${discarded} old queued write(s) belonged to nobody this device can name, and were discarded`,
    );
  }
  // 4. Clean the blob of what was moved or discarded here: its cleaned state is the "done"
  //    marker. A write another tab of an older release added since stays for next time.
  await cleanBlob(env, key, new Set(rows.map((r) => r.id)), settledIn(blob));
  return null;
}

/** The blob's old writes as read: each one moved or discarded by this start. */
function settledIn(blob: StoredClient | undefined): Set<string> {
  const mutations = Array.isArray(blob?.clientState?.mutations)
    ? blob.clientState.mutations
    : [];
  return new Set(mutations.map((m) => JSON.stringify(m)));
}

async function reread(
  env: LegacyWritesEnv,
  key: IDBValidKey,
  stored: StoredClient | undefined,
): Promise<StoredClient | undefined> {
  try {
    return (await env.kv.get(key)) as StoredClient | undefined;
  } catch {
    // Unreadable now: work from what the restore read a moment ago.
    return stored;
  }
}

/** The copy is only-if-absent, so moving the same ones again next time is harmless. */
async function copyRows(
  rows: readonly PendingWrite[],
  env: LegacyWritesEnv,
): Promise<readonly PendingWrite[] | null> {
  try {
    for (const row of rows) await env.pending.putIfAbsent(row);
    return null;
  } catch (err) {
    console.warn(
      `[servwell] ${rows.length} old queued write(s) could not be moved; kept in the saved cache until next time`,
      err,
    );
    return rows;
  }
}

/**
 * The moved rows and the settled old writes leave the blob; carried rows and old writes
 * another tab added since stay for the next start.
 */
async function cleanBlob(
  env: LegacyWritesEnv,
  key: IDBValidKey,
  moved: Set<string>,
  settled: Set<string>,
) {
  try {
    await env.kv.update(key, (current) => {
      if (!current || typeof current !== 'object') return current;
      const cur = current as StoredClient;
      const carried = (cur.legacyWrites ?? []).filter(
        (r) => !moved.has((r as { id?: string }).id ?? ''),
      );
      const mutations = (
        Array.isArray(cur.clientState?.mutations) ? cur.clientState.mutations : []
      ).filter((m) => !settled.has(JSON.stringify(m)));
      const { legacyWrites: _moved, ...rest } = cur;
      return {
        ...rest,
        clientState: { ...cur.clientState, mutations },
        ...(carried.length ? { legacyWrites: carried } : {}),
      };
    });
  } catch (err) {
    // They are in their store now; the next start copies them again (only if absent) and
    // tries the clean once more.
    console.warn(
      '[servwell] old queued writes were moved but the saved cache was not cleaned',
      err,
    );
  }
}

/** The one account every carried row has, if there is exactly one: the device's last. */
function soleAccount(rows: readonly PendingWrite[]): AccountRef | null {
  const first = rows[0]?.madeBy ?? null;
  return first && rows.every((r) => sameAccount(r.madeBy, first)) ? first : null;
}

async function settleNoAccountRefusals(
  account: AccountRef | null,
  failed: FailedWriteStore,
) {
  try {
    const rows = (await failed.all()).filter((row) => row.userId === null);
    for (const row of rows) {
      if (account) {
        const adopted: FailedWrite = {
          ...row,
          userId: account.userId,
          orgId: account.orgId,
          // A guess, and the panel says so: "it may not be yours".
          adopted: true,
        };
        await failed.put(adopted);
      } else {
        await failed.remove(row.mutationId);
      }
    }
  } catch (err) {
    // Left as they are; the next start tries again. Nothing is lost.
    console.warn('[servwell] old refused writes could not be given an account yet', err);
  }
}
